/**
 * Client for the standalone `tour-description-api` Google Cloud Function
 * (format mirrors _the3daapp-fe/cloud-functions/ai-render-api).
 *
 * Takes an address + a list of public pano thumbnail URLs, fetches them,
 * converts to base64, and POSTs to the deployed GCF endpoint. Returns the
 * generated description text.
 *
 * Configure the endpoint in .env:
 *   REACT_APP_TOUR_DESCRIPTION_API_URL=https://<region>-<project>.cloudfunctions.net/generateTourDescription
 */

const MAX_PANOS = 10;

export interface GenerateTourDescriptionInput {
  address: string;
  name?: string;
  panoUrls: string[];
}

export interface GenerateTourDescriptionResult {
  description: string;
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function fetchImageAsInlinePart(
  url: string,
): Promise<{ mimeType: string; data: string } | null> {
  try {
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) return null;
    const mimeType = response.headers.get('content-type') || 'image/jpeg';
    const blob = await response.blob();
    const data = await blobToBase64(blob);
    return { mimeType, data };
  } catch {
    return null;
  }
}

export async function generateTourDescription({
  address,
  name,
  panoUrls,
}: GenerateTourDescriptionInput): Promise<GenerateTourDescriptionResult> {
  const endpoint = process.env.REACT_APP_TOUR_DESCRIPTION_API_URL;
  if (!endpoint) {
    throw new Error('REACT_APP_TOUR_DESCRIPTION_API_URL is not configured');
  }
  if (!address || !address.trim() || address === 'not set') {
    throw new Error('A tour address is required to generate a description.');
  }
  if (!panoUrls.length) {
    throw new Error('This tour has no panoramas to analyze yet.');
  }

  const sampled = panoUrls.slice(0, MAX_PANOS);

  const fetched = await Promise.all(sampled.map(fetchImageAsInlinePart));
  const images = fetched.filter(
    (x): x is { mimeType: string; data: string } => !!x,
  );

  if (!images.length) {
    throw new Error('Could not fetch any panorama images.');
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address, name, images }),
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.message || body?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(
      detail ||
        `Description service returned ${response.status} ${response.statusText}`,
    );
  }

  const payload = await response.json();
  if (!payload?.description) {
    throw new Error('Description service returned an empty response.');
  }

  return { description: payload.description as string };
}
